// PanelState — the one encoding for client-owned panel visibility
// that must survive LiveView patches.
//
// LiveView re-renders server-managed DOM from server state; any
// class or attribute a hook sets on those elements is stripped by
// the next patch that touches the region. Before this helper
// existed, several disclosure hooks each hand-rolled a different
// answer to that problem (or none), so a patch could silently snap a
// panel shut ~100ms after the user opened it.
//
// Two hazards shape the design:
//
//   * Patches strip client state. Open/closed lives in a
//     module-level store keyed by the panel element, never read back
//     from the DOM as source of truth after attach. A MutationObserver
//     re-asserts it when the panel's own region is patched without
//     the hook's element being patched. The observer only re-applies
//     when the DOM disagrees, so applying cannot loop it.
//   * Several hooks can drive one panel (e.g. mobile + desktop nav
//     triggers). Per-hook state would desync — one trigger's observer
//     would immediately undo the other trigger's click. The shared
//     per-panel store makes every trigger read and write one truth.
//
// Hooks whose element is a wrapper CONTAINING the panel do not need
// the observer (a patch of the region fires their own `updated()`);
// they may use plain local state. This module is for the other shape.

export interface PanelState {
  _panelOpen: boolean;
}

export interface SharedPanel {
  /** Current open state (the shared truth, not the DOM). */
  isOpen: () => boolean;
  /** Apply `open` to the panel DOM and record it as the truth. */
  setOpen: (open: boolean) => void;
  /** Flip and apply; returns the new state. */
  toggle: () => boolean;
  /** Re-assert the shared state onto a patched panel. */
  enforce: () => void;
  /** Stop observing the panel. Call from `destroyed()`. */
  disconnect: () => void;
}

const panels = new WeakMap<HTMLElement, { open: boolean }>();

export const attachPanel = (
  panel: HTMLElement | null,
  apply: (open: boolean, panel: HTMLElement) => void,
  matches: (open: boolean, panel: HTMLElement) => boolean,
): SharedPanel => {
  const state = panel
    ? (panels.get(panel) ?? { open: matches(true, panel) })
    : null;
  if (panel && state !== null && !panels.has(panel)) {
    panels.set(panel, state);
  }

  const enforce = (): void => {
    if (panel && state) {
      apply(state.open, panel);
    }
  };

  const observer =
    panel !== null && state !== null && typeof MutationObserver !== "undefined"
      ? new MutationObserver(() => {
          if (!matches(state.open, panel)) {
            enforce();
          }
        })
      : null;

  if (observer !== null && panel !== null) {
    observer.observe(panel, {
      attributes: true,
      childList: false,
      subtree: false,
    });
  }

  return {
    isOpen: (): boolean => state?.open ?? false,
    setOpen: (open: boolean): void => {
      if (state) {
        state.open = open;
      }
      enforce();
    },
    toggle: (): boolean => {
      if (state) {
        state.open = !state.open;
      }
      enforce();
      return state?.open ?? false;
    },
    enforce,
    disconnect: (): void => observer?.disconnect(),
  };
};
